<script setup>
import { useI18n } from 'vue-i18n';
import { computed, onMounted, reactive, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { animate } from 'animejs';
import axios from 'axios';
import ZoomCard from '@/components/Cards/ZoomCard.vue';
import BasicCard from '@/components/Cards/BasicCard.vue';
import LoadingCard from '@/components/Cards/LoadingCard.vue';
import BasicButton from '@/components/Buttons/BasicButton.vue';
import LogoTitleCard from '@/components/Cards/LogoTitleCard.vue';
import BackgroundLayout from '@/layouts/BackgroundLayout.vue';
import { mixins } from '@/Mixins/mixin';
import {
  faCamera,
  faCircleCheck,
  faCircleQuestion,
  faEllipsis,
  faMicrophone,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import PingTest from '@/views/PingTest.vue';
import SettingModal from '@/views/Component/SettingModal.vue';

const { t } = useI18n({ useScope: 'global' });
const source = import.meta.env.VITE_BASE_API ?? 'http://127.0.0.1:10600';
const todaySchedules = reactive({
  loadStatus: 0,
  error: {
    code: 0,
    message: '',
  },
  data: [],
});

const defaultError = t('problem');

const creditsContainer = ref(null);
const creditsContent = ref(null);

const creditRollEffect = () => {
  const containerHeight = creditsContainer.value.offsetHeight;
  const contentHeight = creditsContent.value.offsetHeight;

  animate(creditsContent.value, {
    translateY: [containerHeight, -contentHeight],
    ease: 'linear',
    duration: todaySchedules.data.length * 2500,
    loop: true,
    delay: 500,
  });
};

const getTodaySchedules = async () => {
  todaySchedules.loadStatus = 0;
  await axios
    .get(`${source}/api/pub/today`)
    .then((res) => {
      todaySchedules.loadStatus = 1;
      todaySchedules.data = res.data.content;
    })
    .catch((err) => {
      todaySchedules.loadStatus = -1;
      todaySchedules.error.code = err.response.status;
      todaySchedules.error.message = err.response.data ? err.response.data.error ?? defaultError : defaultError;
    });
};

const mountSchedule = () => {
  getTodaySchedules().then(() => {
    creditRollEffect();
  });
};

// eslint-disable-next-line
const getRange = (item) =>
  mixins.translateDateRange(item.certification_start_at, item.certification_end_at, true, ' s/d ');

const cameraPermission = ref('prompt');
const microphonePermission = ref('prompt');

const requestMediaAccess = async () => {
  try {
    await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    cameraPermission.value = 'granted';
    microphonePermission.value = 'granted';
  } catch (error) {
    if (error.name === 'NotAllowedError') {
      cameraPermission.value = 'denied';
      microphonePermission.value = 'denied';
    }
  }
};

const decideIcon = (permissionStatus) => {
  const icons = {
    prompt: faEllipsis,
    granted: faCircleCheck,
    denied: faTimesCircle,
  };
  return icons[permissionStatus] || faCircleQuestion;
};

const doesAllGranted = computed(() =>
  cameraPermission.value === 'granted' && microphonePermission.value === 'granted'
);

const setting = ref(false);

onMounted(() => {
  mountSchedule();
  mixins.defaultDarkModeCheck();
  requestMediaAccess();
});
</script>

<template>
  <BackgroundLayout ref="layout" left-btn-icon="cog" @left-corner="setting = !setting">
    <SettingModal :show="setting" @close="setting = !setting" />
    <div class="w-full max-w-5xl z-[1]">
      <LogoTitleCard />

      <div class="flex justify-center my-4">
        <BasicCard>
          <PingTest />
        </BasicCard>
      </div>

      <div v-if="todaySchedules.loadStatus === 0" class="all-center">
        <LoadingCard
          :title="`${t('landing.contactingServer')}`"
          color="glass-dark"
          use-img="/img/gif/truck-whoosh.gif"
          with-time
        />
      </div>
      <div v-else-if="todaySchedules.loadStatus === -1" class="all-center">
        <BasicCard>
          <div class="text-center">
            <h5>{{ t('landing.contactFail') }}</h5>
            <p class="text-sm">{{ todaySchedules.error.message }}</p>
          </div>
          <div class="flex justify-center mt-2">
            <BasicButton icon="arrows-rotate" @click="mountSchedule()">{{ t('button.retry') }}</BasicButton>
          </div>
        </BasicCard>
      </div>
      <div v-else class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <!-- Grid 1: Schedules -->
        <div>
          <BasicCard color="glass-bright dark:glass-dark">
            <div class="mb-4 text-center text-white">
              <p class="text-lg font-extrabold">{{ t('landing.todaySchedules') }}</p>
              <p class="text-xs italic font-semibold">{{ mixins.translateDate(new Date(), true) }}</p>
            </div>

            <div
              v-if="todaySchedules.data.length > 0"
              ref="creditsContainer"
              class="overflow-hidden h-[15rem] relative"
            >
              <div ref="creditsContent" class="absolute grid w-full grid-cols-1 gap-2">
                <BasicCard v-for="(sch, i) in todaySchedules.data" :key="i">
                  <h4>{{ sch.sttk_name }}</h4>
                  <div class="text-sm italic">
                    <p v-if="sch.type === 'regular'">{{ t('landing.regularSchedule') }} - {{ getRange(sch) }}</p>
                    <p v-else>
                      {{ sch.company_name ?? t('landing.contractSchedule') }} @
                      <span class="text-primary">{{ sch.name ?? 'PPSDM Migas' }}</span> - {{ getRange(sch) }}
                    </p>
                  </div>
                </BasicCard>
              </div>
            </div>

            <div v-else class="overflow-hidden h-[15rem] relative flex items-center justify-center">
              <BasicCard>
                <div class="text-center">
                  <font-awesome-icon icon="ghost" size="2x" class="truck text-subtitle" />
                  <h4>{{ t('landing.noSchedule') }}</h4>
                </div>
              </BasicCard>
            </div>
          </BasicCard>
        </div>

        <!-- Grid 2: Start button -->
        <div class="grid grid-cols-1 gap-4">
          <div class="all-center">
            <RouterLink v-if="doesAllGranted" :to="{ name: 'login' }">
              <div class="relative w-46 h-44 group">
                <ZoomCard color="alternate-glass" class="overflow-hidden w-52 h-44">
                  <div>
                    <h2>{{ t('landing.start') }}</h2>
                  </div>
                  <img
                    src="/img/gif/test.gif"
                    class="h-40 absolute bottom-[-2.5rem] left-[-2rem] opacity-50"
                    alt="test"
                  />
                </ZoomCard>
                <div
                  class="absolute w-16 h-16 all-center bg-primary rounded-full bottom-[-1rem] right-[-1rem] group-hover:bg-ternary-dark-alt"
                >
                  <p>
                    <font-awesome-icon icon="right-to-bracket" color="white" size="xl" />
                  </p>
                </div>
              </div>
            </RouterLink>
          </div>

          <div class="flex items-center justify-center">
            <BasicCard>
              <p class="mb-2 text-sm text-center">Akses diperlukan:</p>
              <div class="grid grid-cols-2 gap-4 text-center text-gray-600 dark:text-gray-400">
                <div :class="{ 'text-red-600': microphonePermission === 'denied' }">
                  <p><font-awesome-icon :icon="faMicrophone" size="xl" /></p>
                  <p><font-awesome-icon :icon="decideIcon(microphonePermission)" size="sm" /></p>
                </div>
                <div :class="{ 'text-red-600': cameraPermission === 'denied' }">
                  <p><font-awesome-icon :icon="faCamera" size="xl" /></p>
                  <p><font-awesome-icon :icon="decideIcon(cameraPermission)" size="sm" /></p>
                </div>
              </div>
              <div v-if="!doesAllGranted" class="flex justify-center mt-3">
                <BasicButton icon="shield-halved" @click="requestMediaAccess()">
                  Izinkan Akses
                </BasicButton>
              </div>
            </BasicCard>
          </div>
        </div>
      </div>
    </div>
  </BackgroundLayout>
</template>

<style scoped>
.container {
  padding: 2em;
}

.credits-container {
  overflow: hidden;
  height: 300px;
  position: relative;
}
</style>